<template>
  <div>

    <!-- The three buttons and the logo on the top-->

    <b-container class="container-panel">
      <b-row align-v="end">
        <b-col cols="3">
          <b-button v-b-modal="'cardScanInputModal'"
                    class="btn btn-primary btn-lg btn-margin-narrow-aligned-left"
                    title="Scan a payment card (Visa, MasterCard, etc.)"
                    aria-label="Scan a payment card (Visa, MasterCard, etc.)">
            Scan Payment Card
          </b-button>
        </b-col>
        <b-col cols="3">
          <img src="./assets/images/logoSmall.jpg"
               class="logo-margin"
               alt="Ambit Cares Logo"
               aria-label="Ambit Cares Logo" />
        </b-col>
        <b-col cols="6">
          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Submit the donation"
                    :disabled="disableSubmitBtn"
                    aria-label="Submit the donation"
                    @click="onSubmit">
            Submit Donation
          </b-button>

          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Clear the data from the form"
                    aria-label="Clear the data from the form"
                    @click="wipeOutAllInfo">
            Clear Form
          </b-button>

        </b-col>
      </b-row>
    </b-container>

    <b-container class="container-panel">
      <b-row>
        <b-col cols="12">
          <b-alert id="validationErrorsAlert"
                   v-model="alerts.validationErrors"
                   variant="danger"
                   dismissible>
            Please fix any errors on the form!
          </b-alert>
        </b-col>
      </b-row>
    </b-container>

    <!-- The main panels -->

    <b-container class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Donor Information
        </b-col>
      </b-row>

      <b-form>
        <b-container fluid>
          <b-row>
            <b-col cols="2">
              <b-form-group id="donorCategory-group"
                            label="Are you a..."
                            label-for="donorCategoryField">
                <b-form-select id="donorCategoryField"
                               v-model="donor.category"
                               v-validate="'required'"
                               :options="donorCategories"
                               name="donorCategoryField"
                               placeholder="Donor Category" />
                <b-form-invalid-feedback id="donorCategoryField-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group id="donorReferenceNumber-group"
                            label="C# or A#:"
                            label-for="donorReferenceNumberField">
                <b-form-input id="donorReferenceNumberField"
                              v-model="donor.referenceNumber"
                              v-validate="{required: true, regex: cNumberRegex}"
                              :state="validateState('donorReferenceNumberField')"
                              :disabled="cNumberFieldDisabled()"
                              :formatter="formatToUpperCase"
                              name="donorReferenceNumberField"
                              type="text"
                              placeholder="C# or A#"
                              lazy-formatter />
                <b-form-invalid-feedback id="donorReferenceNumberField-live-feedback">
                  Required field - A valid C# or A#
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-form-group id="donorFirstName-group"
                            label="Donor First Name:"
                            label-for="donorFirstNameField">
                <b-form-input id="donorFirstNameField"
                              v-model="donor.firstName"
                              v-validate="{required: true, regex: nameRegex }"
                              :state="validateState('donorFirstNameField')"
                              name="donorFirstNameField"
                              type="text"
                              placeholder="Donor First Name" />
                <b-form-invalid-feedback id="donorFirstNameField-live-feedback">
                  Required field - may not contain special characters
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="donorLastName-group"
                            label="Donor Last Name:"
                            label-for="donorLastNameField">
                <b-form-input id="donorLastNameField"
                              v-model="donor.lastName"
                              v-validate="{required: true, regex: nameRegex }"
                              :state="validateState('donorLastNameField')"
                              name="donorLastNameField"
                              type="text"
                              placeholder="Last Name" />
                <b-form-invalid-feedback id="donorLastNameField-live-feedback">
                  Required field - may not contain special characters
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group id="emailAddress-group"
                            label="Email Address:"
                            label-for="emailAddressField">
                <b-form-input id="emailAddressField"
                              v-model="donor.email"
                              v-validate="'required|email'"
                              :state="validateState('emailAddressField')"
                              name="emailAddressField"
                              type="email"
                              placeholder="Email address" />
                <b-form-invalid-feedback id="emailAddressField-live-feedback">
                  Required: Enter a valid email address
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="phoneNumber-group"
                            label="Phone Number:"
                            label-for="phoneNumberField"
                            description="Phone number, possibly with a country code prefixed with '+'">
                <b-form-input id="phoneNumberField"
                              v-model="donor.phone"
                              v-validate="{regex: phoneRegex }"
                              :state="validateState('phoneNumberField')"
                              name="phoneNumberField"
                              type="text"
                              placeholder="214-555-1234" />
                <b-form-invalid-feedback id="phoneNumberField-live-feedback">
                  Enter a phone number, possibly with a country code preceded by "+"
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-form>
    </b-container>

    <b-container v-if="showCreditCardInfo"
                 class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Credit Card Information
        </b-col>
      </b-row>

      <b-form>
        <b-container fluid>
          <b-row>
            <b-col>
              <b-form-group id="billingFirstName-group"
                            label="Billing First Name:"
                            label-for="billingFirstNameField">
                <b-form-input id="billingFirstNameField"
                              v-model="billing.firstName"
                              v-validate="{required: true, regex: nameRegex }"
                              :state="validateState('billingFirstNameField')"
                              name="billingFirstNameField"
                              type="text"
                              placeholder="Billing First Name" />
                <b-form-invalid-feedback id="billingFirstNameField-live-feedback">
                  Required field - may not contain special characters
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="billingLastName-group"
                            label="Billing Last Name:"
                            label-for="billingLastNameField">
                <b-form-input id="billingLastNameField"
                              v-model="billing.lastName"
                              v-validate="{required: true, regex: nameRegex }"
                              :state="validateState('billingLastNameField')"
                              name="billingLastNameField"
                              type="text"
                              placeholder="Billing Last Name" />
                <b-form-invalid-feedback id="billingLastNameField-live-feedback">
                  Required field - may not contain special characters
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group id="cardNumber-group"
                            label="Card Number:"
                            label-for="cardNumberField">
                <b-form-input id="cardNumberField"
                              v-model="billing.creditCard.numberMasked"
                              v-validate="{required:true, creditcard: {unmasked : this.billing.creditCard.numberActual } }"
                              :state="validateState('cardNumberField')"
                              name="cardNumberField"
                              type="text"
                              placeholder="Credit Card Number"
                              @focus.native="creditCardFocus"
                              @blur.native="creditCardBlur" />
                <b-form-invalid-feedback id="cardNumberField-live-feedback">
                  Required: Enter only numbers
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group id="cardExpMonth-group"
                            label="Card Expiration Month:"
                            label-for="cardExpMonthField">
                <b-form-select id="cardExpMonthField"
                               v-model="billing.creditCard.expMonth"
                               v-validate="'required'"
                               :options="possibleExpirationMonths"
                               name="cardExpMonthField"
                               placeholder="Card Expiration Month" />
                <b-form-invalid-feedback id="cardExpMonthField-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="cardExpYear-group"
                            label="Card Expiration Year:"
                            label-for="cardExpYearField">
                <b-form-select id="cardExpYearField"
                               v-model="billing.creditCard.expYear"
                               v-validate="'required'"
                               :options="possibleExpirationYears"
                               name="cardExpYearField"
                               placeholder="Card Expiration Year" />
                <b-form-invalid-feedback id="cardExpYearField-live-feedback">
                  Required field
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group id="cardCVV-group"
                            label="CVV:"
                            label-for="cardCVVField"
                            description="Three or four digit number">
                <b-form-input id="cardCVVField"
                              v-model="billing.creditCard.cvvMasked"
                              v-validate="{required: true, max:4, regex: cvvRegex }"
                              :state="validateState('cardCVVField')"
                              name="cardCVVField"
                              type="text"
                              placeholder="CVV"
                              title="The 3 or 4 digit Card Verification Value (CVV) number"
                              @focus.native="cvvFocus"
                              @blur.native="cvvBlur" />
                <b-form-invalid-feedback id="cardCVVField-live-feedback">
                  Required field: a three or four digit number
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col>
              <!-- Detailed validation on the Zip/Postal code field is skipped - just pass whatever is entered to the payment processor -->
              <b-form-group id="billingZip-group"
                            label="Zipcode/Postal Code:"
                            label-for="billingZipField">
                <b-form-input id="billingZipField"
                              v-model="billing.creditCard.zipCode"
                              v-validate="'required'"
                              :state="validateState('billingZipField')"
                              :formatter="formatToUpperCase"
                              name="billingZipField"
                              type="text"
                              placeholder="Zip/Postal Code"
                              title="A 5 digit US Zipcode or an international postal code"
                              lazy-formatter />
                <b-form-invalid-feedback id="billingZipField-live-feedback">
                  Required field: A 5-digit US Zipcode or an international postal code
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-form>

    </b-container>
    <b-container class="container-panel outlined-panel">
      <b-row>
        <b-col class="container-panel-heading">
          Donation
        </b-col>
      </b-row>

      <b-form>
        <b-container fluid>
          <b-row>
            <b-col cols="3">
              <b-form-group id="billingAmount-group"
                            label="Amount to Donate (USD):"
                            label-for="billingAmountField">
                <b-form-input id="billingAmountField"
                              v-model="billing.amount"
                              v-validate="'required|numeric|min_value:1|max_value:1500'"
                              :state="validateState('billingAmountField')"
                              name="billingAmountField"
                              type="number"
                              placeholder="Amount" />
                <b-form-invalid-feedback id="billingAmountField-live-feedback">
                  Required: Enter a number between 1 and 1500
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-form>
    </b-container>

    <!-- Repeat the Clear and Submit buttons at the botthm-->

    <b-container class="container-panel">
      <b-row align-v="end">
        <b-col cols="6">
          <!-- no Scan button or Ambit Cares logo in the bottom row - but, take their space -->
        </b-col>
        <b-col cols="6">
          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Submit the donation"
                    :disabled="disableSubmitBtn"
                    aria-label="Submit the donation"
                    @click="onSubmit">
            Submit Donation
          </b-button>

          <b-button class="btn btn-primary btn-lg btn-margin-narrow-aligned-right float-right"
                    title="Clear the data from the form"
                    aria-label="Clear the data from the form"
                    @click="wipeOutAllInfo">
            Clear Form
          </b-button>

        </b-col>
      </b-row>
    </b-container>

    <!-- The various modal windows -->

    <b-modal id="cardScanInputModal"
             v-model="modals.scanCardModalShown"
             title="Card Scanner"
             ok-only
             ok-title="Cancel"
             @shown="setScanCardFocus"
             @hidden="wipeOutAllInfo">
      <div class="modal-body">
        Please Scan your Payment Card.
        <input id="cardInput"
               ref="input"
               v-model="rawCardInput"
               class="card-input-box"
               type="text"
               autofocus
               @keyup.enter="cardScanInput" />

        <span v-show="cardReadErrorMessage"
              class="error-message">
          <br />{{ cardReadErrorMessage }}
        </span>

        <br />
        <label class="check-box-content"
               for="checkbox">
          <input id="isSameAsDonor"
                 v-model="billingNameIsNotSameAsDonor"
                 class="card-scan-modal-checkbox"
                 type="checkbox"
                 name="isSameAsDonor"
                 @click="setScanCardFocus" />
          Click if donor name differs from card holder.
        </label>

      </div>
    </b-modal>

    <!-- Shown after a success Post/Submission -->
    <b-modal id="goodResponseModal"
             ref="goodResponseModal"
             v-model="modals.goodResponseModalShown"
             aria-labelledby="paymentResponseModal"
             title="Donation Submitted Successfully"
             ok-only
             @hidden="wipeOutAllInfo">
      <b-container>
        <b-row>
          <b-col cols="2"
                 style="color: green">
            <font-awesome-icon icon="check-circle"
                               size="3x">
            </font-awesome-icon>
          </b-col>
          <b-col cols="10">
            <span class="modal-head">Thank you!</span><br />
            <span class="modal-note">Your donation has been processed</span>
            <table>
              <tr>
                <td class="modal-note">A receipt will be emailed to: </td>
                <td class="modal-data">{{ modals.goodRes }}</td>
              </tr>
            </table>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>

    <!-- Shown after a failure during the donation submission /post -->
    <b-modal id="errorResponseModal"
             ref="errorResponseModal"
             v-model="modals.errorResponseModalShown"
             aria-labelledby="errorResponseModal"
             title="Error Submitting Data"
             ok-only>
      <b-container>
        <b-row>
          <b-col cols="2"
                 style="color: red">
            <font-awesome-icon icon="exclamation-circle"
                               size="3x">
            </font-awesome-icon>
          </b-col>
          <b-col cols="10">
            <span class="modal-head">Transaction failed</span><br />
            <table>
              <tr>
                <td class="modal-note">Error Code: </td>
                <td class="modal-data">{{ modals.errorRes }}</td>
              </tr>
            </table>
            <span class="modal-note">Please try again</span>
          </b-col>

        </b-row>
      </b-container>
    </b-modal>

  </div>
</template>

<script>

  import utilities from '../utilities';
  import constants from '../constants'

  export default {
    name: 'DonationWithCardScan',
    props: {
      showCreditCardInfo: {
        default: true,
        type: Boolean,
      },
    },

    // the "data" member returns a function that returns an object with the data
    // members of this component, the data members cannot be returned directly
    data() {
      return {
        rawCardInput: '',
        disableSubmitBtn: false,

        donor: {
          fullName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          sendReceiptEmail: '',
          category: 'Consultant',
          referenceNumber: '',
        },

        donorCategories: [
          'Consultant',
          'Customer',
          'Employee',
          'Friend',
        ],

        billing: {
          fullName: '',
          firstName: '',
          lastName: '',
          creditCard: {
            numberMasked: '',
            numberActual: '',
            expMonth: '',
            expYear: '',
            cvvMasked: '',
            cvvActual: '',
            zipCode: '',
          },
          amount: '',
        },
        billingNameIsNotSameAsDonor: false,

        possibleExpirationMonths: utilities.loadExpiryMonths(),
        possibleExpirationYears: utilities.loadExpiryYears(),

        cardReadErrorMessage: '',

        scanCardModalShow: false,

        modals: {
          errorRes: '',
          goodRes: '',
          scanCardModalShown: false,
          goodResponseModalShown: false,
          errorResponseModalShown: false,
        },

        alerts: {
          validationErrors: false,
        },

        dataFromCardScan: false,

        nameRegex: constants.nameRegex,
        cvvRegex: constants.cvvRegex,
        phoneRegex: constants.phoneRegex,
        cNumberRegex: constants.cNumberRegex,

      };
    },

    methods: {

      // -----------------------------------------------------------------------
      // Called with the user presses the "Scan Payment Card" button
      // reads the information from the magnetic strip on the card and
      // populates several fields on the form
      // -----------------------------------------------------------------------
      cardScanInput() {
        try {
          const retainIsNotSameAsDonor = true;
          this.wipeOutBillingInfo(retainIsNotSameAsDonor);
          this.dataFromCardScan = true;

          if (this.rawCardInput.startsWith('%E') || this.rawCardInput.includes('E?') || !this.rawCardInput.startsWith('%B')) {
            this.cardReadErrorMessage = 'Error reading card - please try again';
            this.rawCardInput = '';
            return;
          }

          this.cardReadErrorMessage = '';

          const cardInfo = this.rawCardInput.match(/%B(\d+)\^(.*)\^(\d{4}).*/);
          const userInfo = cardInfo[2].match(/(\w*)\/(.*)/);
          const cardExp = cardInfo[3].match(/(\d{2})(\d{2})/);
          const currentYearFront2 = new Date().getFullYear().toString().slice(0, 2);

          if (!this.billingNameIsNotSameAsDonor || !this.showCreditCardInfo) {
            this.donor.lastName = userInfo[1];
            if (userInfo[2]) {
              this.donor.firstName = userInfo[2].trim();
              this.donor.fullName = `${this.donor.firstName} ${this.donor.lastName}`;
            } else {
              this.donor.fullName = this.donor.lastName;
            }
          }

          if (this.showCreditCardInfo) {
            this.billing.creditCard.numberActual = cardInfo[1].toString();
            this.billing.creditCard.numberMasked = cardInfo[1].toString();
            this.billing.creditCard.expMonth = cardExp[2];
            this.billing.creditCard.expYear = `${currentYearFront2}${cardExp[1]}`;

            this.billing.lastName = userInfo[1];
            if (userInfo[2]) {
              this.billing.firstName = userInfo[2].trim();
              this.billing.fullName = `${this.billing.firstName} ${this.billing.lastName}`;
            } else {
              this.billing.fullName = this.billing.lastName;
            }

            // make sure the credit card number is masked
            this.creditCardBlur();
          }

          this.rawCardInput = '';

          // highlight required fields as being required
          this.$validator.validate('donorFirstNameField');
          this.$validator.validate('donorLastNameField');
          this.$validator.validate('donorReferenceNumberField');
          this.$validator.validate('emailAddressField');
          this.$validator.validate('billingAmountField');
          if (this.showCreditCardInfo) {
            this.$validator.validate('cardCVVField');
            this.$validator.validate('billingZipField');
          }

          // close the window (by saying "it's not shown any more")
          this.modals.scanCardModalShown = false;
        } catch (error) {
          this.cardReadErrorMessage = 'Error reading card - please try again';
          this.rawCardInput = '';
        }
      },

      // -----------------------------------------------------------------------
      // These two function handle pressing the submit button and uploading the
      // data from the form to the server
      // -----------------------------------------------------------------------
      onSubmit() {
        const self = this;
        this.alerts.validationErrors = false;
        self.disableSubmitBtn = true;

        self.$validator.validateAll().then(
          (result) => {
            if (result) {
              self.processDonation();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              this.alerts.validationErrors = true;
              self.disableSubmitBtn = false;
            }
          },
        );
      },


      // -----------------------------------------------------------------------
      // Looks at the donor category and at the donor number (donor.referenceNumber)
      // and figures out what the donor number should be.  The number returned
      // will always be prefaced with the appropriate letter (C, A, or E).  In the
      // case of a "Friend" or "Employee, the donor number will be the donor's full name
      // If there is bad data, then the returned value will be a string prefaced with
      // the work "BAD"
      // -----------------------------------------------------------------------
      figureDonorNumber() {
        // The "cNumberFieldDisabled" field is disabled for Employees and Friends
        // The transaction requires a Donor Number, so we swap in the Full Name as that "number"
        if (this.cNumberFieldDisabled()) {
          return this.donor.fullName;
        }
        const upperValue = this.donor.referenceNumber.toUpperCase();
        const firstChar = upperValue.substring(0, 1);
        let donorTypeLetter = '';
        if (this.donor.category === 'Customer') {
          donorTypeLetter = 'A';
          if (firstChar === 'A') {
            const customerPrefix = upperValue.substring(0, 2);
            if (customerPrefix === 'AJ' || customerPrefix === 'AC') {
              if (Number.isNaN(upperValue.substring(3))) {
                return 'BAD - Incorrectly formatted donor number';
              }
              // otherwise, it looks like AC9999, so just return it
              return upperValue;
            }
          }
        } else {
          donorTypeLetter = this.donor.category.substring(0, 1).toUpperCase();
        }
        let numberPart = NaN;
        if ('AC'.indexOf(firstChar) >= 0) {
          if (firstChar !== donorTypeLetter) {
            return 'BAD - Donor Type Letter mis-match';
          }
          numberPart = Number(this.donor.referenceNumber.substring(1));
        } else {
          numberPart = Number(this.donor.referenceNumber);
        }
        if (Number.isNaN(numberPart)) {
          return 'BAD - Incorrectly formatted donor number';
        }
        return donorTypeLetter + numberPart;
      },

      processDonation() {
        const url = this.getPostUrl();
        const self = this;
        this.donor.fullName = this.donor.fullName || `${this.donor.firstName} ${this.donor.lastName}`;
        this.billing.fullName = this.billing.fullName || `${this.billing.firstName} ${this.billing.lastName}`;
        let donorNum = '';

        // The donorNumber may end up being the full name (in the cases of Employees and Friends)
        donorNum = this.figureDonorNumber();

        if (donorNum.startsWith('BAD -')) {
          self.modals.errorRes = 'Donor number badly formatted';
          self.$refs.errorResponseModal.show();
          self.disableSubmitBtn = false;
          return;
        }

        // remove dashes, parentheses and spaces from the phone number (leave in any "+")
        const phoneRegex = new RegExp(/[- ()]/g);
        const phoneNum = this.donor.phone.replace(phoneRegex, '');

        const postData = {
          BillAmount: this.billing.amount,
          BillingAddress: '2019 Ambition',
          BillingCity: 'AmbitCares',
          BillingFullName: this.billing.fullName,
          BillingPhoneNumber: phoneNum,
          BillingState: 'TX',
          BillingZip: this.billing.creditCard.zipCode,
          CreditCardNumber: this.billing.creditCard.numberActual,
          DonorNumber: donorNum,
          DonorType: this.donor.category,
          EmailAddress: this.donor.email,
          EntityType: 'Individual',
          ExpiryMonth: this.billing.creditCard.expMonth,
          ExpiryYear: this.billing.creditCard.expYear,
          IpAddress: '',
          LocationCode: '',
          SecurityCode: this.billing.creditCard.cvvActual,
          SendReceiptEmail: true,
        };

        this.axios.post(url, postData)
          .then((res) => {
            if (res.status === 200) {
              if (!res.data.isError) {
                // the happy path
                self.modals.goodRes = self.donor.email;
                self.$refs.goodResponseModal.show();
                self.wipeOutAllInfo();
                self.disableSubmitBtn = false;
              } else {
                // if we get a 200, but we still get isError true...
                self.modals.errorRes = res.data.message || res.data.ResponseMessage;
                self.$refs.errorResponseModal.show();
                self.disableSubmitBtn = false;
              }
            } else if (res.status === 400 || res.status === 500) {
              self.modals.errorRes = res.data.message || res.data.ResponseMessage;
              self.$refs.errorResponseModal.show();
              self.disableSubmitBtn = false;
            } else {
              self.modals.errorRes = res.data.message || res.data.ResponseMessage;
              self.$refs.errorResponseModal.show();
              self.disableSubmitBtn = false;
            }
          }).catch(() => {
            self.modals.errorRes = 'Unexpected error processing payment.';
            self.$refs.errorResponseModal.show();
            self.disableSubmitBtn = false;
          });
      },

      // -----------------------------------------------------------------------
      // Sets the focus to the "hidden" card input field when the
      // "Scan Payment Card" modal dialog pops up.
      // -----------------------------------------------------------------------

      setScanCardFocus() {
        const input = document.getElementById('cardInput');
        input.focus();
      },

      // -----------------------------------------------------------------------
      // These four functions managing masking out the CVV field and all but
      // the last four digits of the credit card number (when focus is not)
      // on the field in question
      // -----------------------------------------------------------------------

      cvvFocus() {
        this.billing.creditCard.cvvMasked = utilities.unmaskField(this.billing.creditCard.cvvActual, this.billing.creditCard.cvvMasked);
      },

      cvvBlur() {
        [this.billing.creditCard.cvvActual, this.billing.creditCard.cvvMasked] = utilities.maskCvv(
          this.billing.creditCard.cvvActual,
          this.billing.creditCard.cvvMasked,
        );
      },

      creditCardFocus() {
        this.billing.creditCard.numberMasked = utilities.unmaskField(
          this.billing.creditCard.numberActual,
          this.billing.creditCard.numberMasked,
        );
      },

      creditCardBlur() {
        [this.billing.creditCard.numberActual, this.billing.creditCard.numberMasked] = utilities.maskCreditCard(
          this.billing.creditCard.numberActual,
          this.billing.creditCard.numberMasked,
        );
      },

      // -----------------------------------------------------------------------
      // When a user choose either "Employee" or "Friend" from the list (i.e.,
      // not "Consultant" or "Customer"), then the "C-Number" should be disabled
      // -----------------------------------------------------------------------

      cNumberFieldDisabled() {
        const isDisabled = this.donor.category === 'Friend' || this.donor.category === 'Employee';
        return isDisabled;
      },

      // -----------------------------------------------------------------------
      // These functions wipe out appropriate fields (and remove any validation
      // errors)
      // -----------------------------------------------------------------------

      wipeOutBillingInfo(retainIsNotSameAsDonor) {
        this.billingNameIsNotSameAsDonor = retainIsNotSameAsDonor ? this.billingNameIsNotSameAsDonor : false;
        this.billing = {
          fullName: '',
          firstName: '',
          lastName: '',
          creditCard: {
            numberActual: '',
            numberMasked: '',
            expMonth: '',
            expYear: '',
            cvvMasked: '',
            cvvActual: '',
            zipCode: '',
          },
          amount: '',
        };
      },

      wipeOutDonorInfo() {
        this.donor = {
          fullName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          sendReceiptEmail: '',
          category: 'Consultant',
          referenceNumber: '',
        };
      },

      wipeOutAllInfo() {
        this.cardReadErrorMessage = '';

        // make sure you don't wipe out the information from a card scan as the modal closes
        if (this.dataFromCardScan) {
          this.dataFromCardScan = false;
          return;
        }
        const retainIsNotSameAsDonor = false;

        this.wipeOutBillingInfo(retainIsNotSameAsDonor);
        this.wipeOutDonorInfo();
        // clean up all the "required field" errors that will fire when the fields are cleared
        this.veeErrors.clear();
        this.$validator.reset();
        // re-enable the submit button if it got stuck disabled
        this.disableSubmitBtn = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },

      // -----------------------------------------------------------------------
      // this is called by every field as part of the live validation process
      // Returns one of:
      //    false:  shows a red mark
      //    true:   shows a green mark
      //    null:   no mark shown
      // -----------------------------------------------------------------------
      validateState(ref) {
        // if the field exists
        if (this.veeFields[ref]
            // and is either "dirty" or "validated" (i.e., touched)
          && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
            // return an indicator if it's good (true) or bad (false)
          return !this.veeErrors.has(ref);
        }
        // otherwise - it hasn't been touched, so leave it alone
        return null;
      },


      // -----------------------------------------------------------------------
      // Canadian postal codes look like "A9A 9A9" (alternationg alpha and numberics,
      // in two groups of three, upper case)
      // Make sure that the entered code gets converted to upper case.
      // Also used to upper-case the C-Number field
      // -----------------------------------------------------------------------
      formatToUpperCase(value) {
        return value.toUpperCase();
      },

      // -----------------------------------------------------------------------
      // Used to get the URL to post to
      // -----------------------------------------------------------------------

      getPostUrl() {
        // at one point, this looked up the environment (with location.hostname)
        // and returned a URL.  Right now, it always just returns...
        const endpointUri = '/api/donordonation/makedonationpayment';
        return endpointUri;
      },
    },
  };

</script>

<style src="./assets/css/CommonVue.css" scoped>
</style>

<style scoped>
</style>
